import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { CalendarClock } from "lucide-react";
import type { FlexxiPlanStatus } from "@gaole/shared";
import { api } from "../../lib/api";
import { fmtDateDMY, rm } from "../../lib/format";
import { EmptyState, PageLoader } from "../../components/ui";
import { PageHeader } from "../../components/storefront/PageHeader";
import { ProductImage } from "../../components/ProductCard";
import { FlexxiProgressBar, FlexxiStatusBadge } from "../../components/flexxi/FlexxiPlanProgress";

export interface FlexxiPlanListRow {
  id: string;
  status: FlexxiPlanStatus;
  statusLabel: string;
  productName: string;
  productImage: string | null;
  totalPayable: number;
  paidTotal: number;
  outstanding: number;
  progressPercent: number;
  templateName: string;
  termMonths: number;
  createdAt: string;
  nextDue: { dueDate: string; amount: number } | null;
}

export function FlexxiPlans() {
  const q = useQuery({
    queryKey: ["flexxi", "plans"],
    queryFn: () => api<{ plans: FlexxiPlanListRow[] }>("/flexxi/plans"),
  });

  if (q.isLoading) return <PageLoader />;
  const plans = q.data?.plans ?? [];
  const totals = plans.reduce(
    (acc, p) => ({ paid: acc.paid + p.paidTotal, outstanding: acc.outstanding + p.outstanding }),
    { paid: 0, outstanding: 0 },
  );

  return (
    <div className="space-y-4 px-4 pb-8 pt-4 lg:px-6">
      <PageHeader title="My FlexxiPlans" description="Track instalments, payments and receipts." />

      {plans.length ? (
        <div className="grid grid-cols-2 gap-3">
          <div className="glass-panel rounded-2xl p-4">
            <p className="text-[10px] uppercase tracking-wide text-white/50">Total paid</p>
            <p className="font-display text-lg font-bold tabular-nums">{rm(totals.paid)}</p>
          </div>
          <div className="glass-panel rounded-2xl p-4">
            <p className="text-[10px] uppercase tracking-wide text-white/50">Outstanding</p>
            <p className="font-display text-lg font-bold tabular-nums text-brand-pink">{rm(totals.outstanding)}</p>
          </div>
        </div>
      ) : null}

      {!plans.length ? (
        <EmptyState
          icon={CalendarClock}
          title="No FlexxiPlans yet"
          hint="Choose Pay with Instalment on an eligible shop item to start one."
          action={
            <Link to="/shop" className="text-sm font-semibold text-brand-pink underline">
              Browse the shop
            </Link>
          }
        />
      ) : (
        <ul className="space-y-3">
          {plans.map((p) => (
            <li key={p.id}>
              <Link
                to={`/account/flexxi/${p.id}`}
                className="glass-panel flex gap-3 rounded-2xl p-3 transition hover:bg-white/[0.04]"
              >
                <div className="h-20 w-16 shrink-0 overflow-hidden rounded-xl bg-surface-2">
                  <ProductImage src={p.productImage} alt={p.productName} className="h-full w-full" />
                </div>
                <div className="min-w-0 flex-1 space-y-1.5">
                  <div className="flex items-start justify-between gap-2">
                    <p className="truncate text-sm font-semibold">{p.productName}</p>
                    <FlexxiStatusBadge status={p.status} label={p.statusLabel} />
                  </div>
                  <p className="text-[11px] text-white/50">
                    {p.templateName} · started {fmtDateDMY(p.createdAt)}
                  </p>
                  <FlexxiProgressBar percent={p.progressPercent} />
                  <div className="flex justify-between gap-2 text-[11px] text-white/60">
                    <span className="tabular-nums">
                      {rm(p.paidTotal)} / {rm(p.totalPayable)}
                    </span>
                    {p.nextDue ? (
                      <span className="tabular-nums">
                        Next {rm(p.nextDue.amount)} · {fmtDateDMY(p.nextDue.dueDate)}
                      </span>
                    ) : (
                      <span>{p.progressPercent}% paid</span>
                    )}
                  </div>
                </div>
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
